import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  FloppyDisk,
  Trash,
  ArrowLeft,
  TreeStructure,
} from '@phosphor-icons/react';
import {
  SegmentExpression,
  SegmentExpressionGroup,
  SegmentUnit,
  SEGMENT_LABELS,
  SEGMENT_LABEL_CONFIG,
  isSegmentClusterCondition,
} from 'shared/types/segment';
import {
  useSegment,
  useCreateSegment,
  useUpdateSegment,
  useDeleteSegment,
  useEvaluateSegment,
  useClusters,
} from 'hooks/useSegments';
import SegmentExpressionBuilder from './components/SegmentExpressionBuilder';
import {
  SEGMENT_UNITS_COLUMNS,
  SEGMENT_UNITS_SORT,
} from './components/SegmentUnitsTableConfig';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Spinner } from '@/components/ui/spinner';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { UniversalTable, CellConfig } from '@/shared/components/UniversalTable';

export default function SegmentDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isNewSegment = id === 'new';

  // Hooks
  const { segment, isLoading: isLoadingSegment } = useSegment(id);
  const { createSegment, isCreating } = useCreateSegment();
  const { updateSegment, isUpdating } = useUpdateSegment();
  const { deleteSegment, isDeleting } = useDeleteSegment();
  const { result: evaluationResult, isLoading: isEvaluating, refetch: reEvaluate } = useEvaluateSegment(id);
  const { clusters, isLoading: isLoadingClusters } = useClusters();

  // Form state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [expression, setExpression] = useState<SegmentExpression>({
    type: 'group',
    logicalOperator: 'AND',
    conditions: [],
  });
  const [labels, setLabels] = useState<string[]>([]);
  const [active, setActive] = useState(true);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [unitSearchValue, setUnitSearchValue] = useState('');

  // Load segment data into form
  useEffect(() => {
    if (segment) {
      setName(segment.name || '');
      setDescription(segment.description || '');
      setExpression(
        segment.expression || {
          type: 'group',
          logicalOperator: 'AND',
          conditions: [],
        },
      );
      setLabels(segment.labels || []);
      setActive(segment.active ?? true);
    }
  }, [segment]);

  const isSaving = isCreating || isUpdating;

  // Count total cluster conditions in expression (recursive)
  const countConditions = (group: SegmentExpressionGroup): number => {
    let count = 0;
    for (const cond of group.conditions) {
      if (isSegmentClusterCondition(cond)) {
        count++;
      } else {
        count += countConditions(cond);
      }
    }
    return count;
  };

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error('Voer een naam in voor het segment');
      return;
    }

    if (countConditions(expression) === 0) {
      toast.error('Voeg minstens één cluster toe aan de expressie');
      return;
    }

    try {
      if (isNewSegment) {
        const created = await createSegment({
          name: name.trim(),
          description: description.trim() || undefined,
          expression,
          labels,
          active,
        });
        toast.success('Segment aangemaakt');
        navigate(`/segments/${created.id}`, { replace: true });
      } else {
        await updateSegment(Number(id), {
          name: name.trim(),
          description: description.trim() || undefined,
          expression,
          labels,
          active,
        });
        toast.success('Segment opgeslagen');
        reEvaluate();
      }
    } catch (error) {
      console.error('Error saving segment:', error);
      toast.error(
        error instanceof Error ? error.message : 'Kon segment niet opslaan',
      );
    }
  };

  const handleDelete = async () => {
    if (!id || isNewSegment) return;
    try {
      await deleteSegment(Number(id));
      toast.success('Segment verwijderd');
      navigate('/segments');
    } catch (error) {
      console.error('Error deleting segment:', error);
      toast.error('Kon segment niet verwijderen');
    } finally {
      setDeleteConfirmOpen(false);
    }
  };

  const toggleLabel = (label: string) => {
    setLabels((prev) =>
      prev.includes(label) ? prev.filter((l) => l !== label) : [...prev, label],
    );
  };

  // Unit table row renderer (same pattern as clusterDetail.tsx)
  const renderUnitRow = useCallback((row: SegmentUnit): CellConfig[] => [
    {
      id: 'unitId',
      content: (
        <span
          className="font-medium text-foreground text-sm cursor-default truncate"
          title={row.building?.googlePlaceLongAddress || ''}
        >
          {row.boxNumber || row.id.slice(0, 8)}
        </span>
      ),
    },
    {
      id: 'referencePlus',
      content: (
        <span className="text-sm">{row.referencePlus || '—'}</span>
      ),
    },
    {
      id: 'unitType',
      content: (
        <div className="flex items-center gap-1 flex-wrap">
          {row.unitType ? (
            <Badge variant="secondary" className="text-xs">{row.unitType}</Badge>
          ) : (
            <span className="text-xs text-muted-foreground">—</span>
          )}
          {row.unitSubtype && (
            <Badge variant="outline" className="text-xs">{row.unitSubtype}</Badge>
          )}
        </div>
      ),
    },
    {
      id: 'floor',
      content: row.floor != null ? (
        <span className="text-sm">{row.floor}</span>
      ) : (
        <span className="text-xs text-muted-foreground">—</span>
      ),
    },
    {
      id: 'grossSurface',
      content: row.grossSurface != null ? (
        <span className="text-sm">{row.grossSurface}</span>
      ) : (
        <span className="text-xs text-muted-foreground">—</span>
      ),
    },
    {
      id: 'netSurface',
      content: row.netSurface != null ? (
        <span className="text-sm">{row.netSurface}</span>
      ) : (
        <span className="text-xs text-muted-foreground">—</span>
      ),
    },
    {
      id: 'building',
      content: (
        <span className="text-sm truncate">
          {row.building?.googlePlaceLongAddress || '—'}
        </span>
      ),
    },
    {
      id: 'company',
      content: row.building?.company ? (
        <Badge variant="outline" className="text-xs">{row.building.company.name}</Badge>
      ) : (
        <span className="text-xs text-muted-foreground">—</span>
      ),
    },
  ], []);

  if (isLoadingSegment && !isNewSegment) {
    return (
      <div className="flex flex-col justify-center items-center h-[60vh] gap-3">
        <Spinner className="size-8 text-brick-primary" />
        <p className="text-muted-foreground text-sm">Segment laden...</p>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 max-w-7xl space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
        <div className="flex items-start gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/segments')}
            className="mt-1"
          >
            <ArrowLeft className="size-5" />
          </Button>
          <div className="space-y-1">
            <h1 className="text-2xl font-semibold text-foreground">
              {isNewSegment
                ? 'Nieuw Segment'
                : segment?.name || 'Segment Details'}
            </h1>
            <p className="text-muted-foreground">
              {isNewSegment
                ? 'Definieer een nieuw segment met clusters'
                : 'Beheer de expressie en labels van dit segment'}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={handleSave}
            disabled={isSaving || !name.trim()}
            className="bg-brick-primary hover:bg-brick-primary/90 text-white"
          >
            {isSaving && <Spinner className="size-4" />}
            <FloppyDisk className="size-4" />
            Opslaan
          </Button>
          {!isNewSegment && (
            <Button
              variant="outline"
              onClick={() => setDeleteConfirmOpen(true)}
              className="text-destructive hover:text-destructive hover:bg-destructive/10"
            >
              <Trash className="size-4" />
              Verwijderen
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column */}
        <div className="lg:col-span-2 space-y-6">
          {/* General Info */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TreeStructure
                  className="size-5 text-brick-primary"
                  weight="duotone"
                />
                Algemene informatie
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <label
                  htmlFor="segment-name"
                  className="text-sm font-medium text-foreground"
                >
                  Segment naam
                </label>
                <Input
                  id="segment-name"
                  placeholder="Bijv. Appartementen Antwerpen"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  autoFocus={isNewSegment}
                />
              </div>
              <div className="space-y-2">
                <label
                  htmlFor="segment-description"
                  className="text-sm font-medium text-foreground"
                >
                  Beschrijving
                </label>
                <Textarea
                  id="segment-description"
                  placeholder="Optionele beschrijving van het segment..."
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={3}
                />
              </div>
            </CardContent>
          </Card>

          {/* Labels */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base">Labels</CardTitle>
                  <CardDescription>
                    Categoriseer het segment met labels
                  </CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">
                    {active ? 'Actief' : 'Inactief'}
                  </span>
                  <Switch checked={active} onCheckedChange={setActive} />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {SEGMENT_LABELS.map((label) => {
                  const config = SEGMENT_LABEL_CONFIG[label];
                  const isSelected = labels.includes(label);
                  return (
                    <Badge
                      key={label}
                      variant={isSelected ? 'default' : 'outline'}
                      className={`cursor-pointer transition-all ${
                        isSelected
                          ? config?.color || 'bg-brick-primary text-white'
                          : 'hover:bg-muted'
                      }`}
                      onClick={() => toggleLabel(label)}
                    >
                      {config?.label || label}
                    </Badge>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Expression Builder */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TreeStructure
                  className="size-5 text-moss-primary"
                  weight="duotone"
                />
                Segment expressie
              </CardTitle>
              <CardDescription>
                Definieer welke units tot dit segment behoren door clusters te
                combineren met EN/OF logica
              </CardDescription>
            </CardHeader>
            <CardContent>
              {isLoadingClusters ? (
                <div className="flex justify-center py-8">
                  <Spinner className="size-6 text-brick-primary" />
                </div>
              ) : (
                <SegmentExpressionBuilder
                  value={expression}
                  onChange={setExpression}
                  clusters={clusters}
                />
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right Column */}
        <div className="space-y-6">
          {/* Overview */}
          {!isNewSegment && segment && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Overzicht</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div className="text-center p-3 bg-muted/50 rounded-lg">
                    <div className="text-2xl font-bold text-brick-primary">
                      {evaluationResult?.unitCount ?? segment.unitCount ?? '—'}
                    </div>
                    <div className="text-xs text-muted-foreground">Units</div>
                  </div>
                  <div className="text-center p-3 bg-muted/50 rounded-lg">
                    <div className="text-2xl font-bold text-moss-primary">
                      {segment.active ? 'Actief' : 'Inactief'}
                    </div>
                    <div className="text-xs text-muted-foreground">Status</div>
                  </div>
                </div>

                <Separator />

                <div className="text-xs text-muted-foreground space-y-1">
                  <p>
                    Aangemaakt:{' '}
                    {new Date(segment.createdAt).toLocaleDateString('nl-NL', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric',
                    })}
                  </p>
                  <p>
                    Bijgewerkt:{' '}
                    {new Date(segment.updatedAt).toLocaleDateString('nl-NL', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric',
                    })}
                  </p>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Units Table — full width below the grid */}
      {!isNewSegment && (
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-base flex items-center gap-2">
                  <TreeStructure
                    className="size-4 text-brick-primary"
                    weight="duotone"
                  />
                  Overeenkomende units
                </CardTitle>
                <CardDescription>
                  {isEvaluating
                    ? 'Evalueren...'
                    : `${evaluationResult?.unitCount ?? 0} units`}
                </CardDescription>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={reEvaluate}
                disabled={isEvaluating}
                className="text-xs"
              >
                {isEvaluating ? (
                  <Spinner className="size-3" />
                ) : (
                  'Vernieuwen'
                )}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {isEvaluating ? (
              <div className="flex justify-center py-8">
                <Spinner className="size-6 text-brick-primary" />
              </div>
            ) : evaluationResult?.units && evaluationResult.units.length > 0 ? (
              <UniversalTable
                rows={evaluationResult.units}
                title=""
                columnsConfig={SEGMENT_UNITS_COLUMNS}
                sortConfig={SEGMENT_UNITS_SORT}
                renderRow={renderUnitRow}
                getRowKey={(row: SegmentUnit) => row.id}
                searchConfig={{
                  searchPlaceholder: 'Zoek units...',
                  searchValue: unitSearchValue,
                  onSearchChange: setUnitSearchValue,
                  showSpacer: false,
                  serverSideFiltering: true,
                }}
                onRowClick={(row: SegmentUnit) => {
                  if (row.building?.id) {
                    navigate(`/building/${row.building.id}`);
                  }
                }}
              />
            ) : (
              <div className="flex flex-col items-center justify-center py-8 rounded-lg border-2 border-dashed border-border/50">
                <TreeStructure
                  className="size-8 text-muted-foreground/50 mb-2"
                  weight="duotone"
                />
                <p className="text-muted-foreground text-sm text-center">
                  Geen units gevonden. Voeg clusters toe aan de expressie.
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Delete Confirmation */}
      <AlertDialog
        open={deleteConfirmOpen}
        onOpenChange={setDeleteConfirmOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Segment verwijderen?</AlertDialogTitle>
            <AlertDialogDescription>
              Weet je zeker dat je het segment &quot;{segment?.name}&quot; wilt
              verwijderen? Deze actie kan niet ongedaan worden gemaakt.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>
              Annuleren
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={isDeleting}
              className="bg-destructive hover:bg-destructive/90"
            >
              {isDeleting && <Spinner className="size-4" />}
              Verwijderen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
